import { useCallback, useMemo } from "react";
import { cn } from "@/utils/cn";
import {
  parseAsString,
  parseAsStringLiteral,
  useQueryState,
  useQueryStates,
} from "nuqs";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import AddConnectorDialog from "./components/AddConnectorDialog";
import DeleteConnectionDialog from "./components/DeleteConnectionDialog";
import MCPDetailPanel from "./components/MCPDetailPanel";
import MCPList from "./components/MCPList";
import MCPToolbar from "./components/MCPToolbar";
import { mapDtoToFormValues, mapFormToCreatePayload } from "./adapters";
import { openOAuthPopup, settleOAuthPopup } from "./oauth";
import { useCreateMCPConnection } from "./hooks/useCreateMCPConnection";
import { useDeleteMCPConnection } from "./hooks/useDeleteMCPConnection";
import { useMCPConnection } from "./hooks/useMCPConnection";
import { useMCPConnections } from "./hooks/useMCPConnections";
import { useUpdateMCPConnection } from "./hooks/useUpdateMCPConnection";
import { usePanePresence } from "./usePanePresence";
import type { ConnectorFormValues } from "./types";

// The page has exactly one "active surface" at a time. Keeping that in the URL
// gives us:
//   - a single source of truth (no juggling 4 useStates)
//   - back-button + shareable links for free
//   - mutual exclusion by construction (no two overlays fighting)
const VIEW_VALUES = ["drawer", "edit", "create", "delete"] as const;
type View = (typeof VIEW_VALUES)[number];

const MCPsPage = () => {
  const { t } = useTranslation();
  const { connections, isLoading, error } = useMCPConnections();

  const [query, setQuery] = useQueryState("q", parseAsString.withDefault(""));
  const [{ view, id }, setUrlState] = useQueryStates({
    view: parseAsStringLiteral(VIEW_VALUES),
    id: parseAsString,
  });

  // Derive every surface's state from the URL. No local copies.
  const selectedId = view === "drawer" ? id : null;
  const editingId = view === "edit" ? id : null;
  const pendingDeleteId = view === "delete" ? id : null;
  const isAddOpen = view === "create";
  const dialogOpen = view === "create" || view === "edit";
  const dialogMode: "create" | "edit" =
    view === "edit" ? "edit" : "create";

  // Pulls the row from the shared cache so the dialog can pre-fill instantly
  // when the user picks Edit from the list menu.
  const { dto: editingDto } = useMCPConnection(editingId);

  const closeAll = useCallback(() => {
    setUrlState({ view: null, id: null });
  }, [setUrlState]);

  const openView = useCallback(
    (next: View, targetId: string | null = null) => {
      setUrlState({ view: next, id: targetId });
    },
    [setUrlState],
  );

  const createMutation = useCreateMCPConnection({
    onSuccess: () => {
      if (isAddOpen) closeAll();
    },
  });
  const updateMutation = useUpdateMCPConnection({
    onSuccess: () => {
      if (view === "edit") closeAll();
    },
  });
  const deleteMutation = useDeleteMCPConnection({
    onSuccess: (deletedId) => {
      // If anything else was pointing at the same row, close it.
      if (id === deletedId) closeAll();
    },
  });

  const filtered = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return connections;
    return connections.filter((connection) =>
      connection.name.toLowerCase().includes(trimmed),
    );
  }, [query, connections]);

  const pendingDeleteConnection = useMemo(
    () =>
      pendingDeleteId
        ? connections.find((c) => c.id === pendingDeleteId) ?? null
        : null,
    [pendingDeleteId, connections],
  );

  const editInitialValues = useMemo(() => {
    if (!editingDto) return undefined;
    try {
      return mapDtoToFormValues(editingDto);
    } catch (err) {
      console.error("[MCPs] mapDtoToFormValues threw", err, "dto:", editingDto);
      return undefined;
    }
  }, [editingDto]);

  const dialogSubmitting =
    dialogMode === "edit"
      ? updateMutation.isPending
      : createMutation.isPending;

  const handleSelect = useCallback(
    (rowId: string) => openView("drawer", rowId),
    [openView],
  );
  const handleEdit = useCallback(
    (rowId: string) => openView("edit", rowId),
    [openView],
  );
  const handleDelete = useCallback(
    (rowId: string) => openView("delete", rowId),
    [openView],
  );
  const handleNewConnection = useCallback(
    () => openView("create"),
    [openView],
  );

  const handleConfirmDelete = useCallback(() => {
    if (pendingDeleteId) deleteMutation.mutate(pendingDeleteId);
  }, [pendingDeleteId, deleteMutation]);

  const handleDialogOpenChange = useCallback(
    (next: boolean) => {
      if (!next) closeAll();
    },
    [closeAll],
  );

  const handleDialogSubmit = useCallback(
    (values: ConnectorFormValues) => {
      const payload = mapFormToCreatePayload(values);
      if (dialogMode === "edit" && editingId) {
        updateMutation.mutate({ id: editingId, payload });
        return;
      }

      // Pre-open the OAuth tab synchronously inside the submit click so the
      // browser allows it (blockers reject window.open after the await).
      const popup = values.authHeader === "oauth" ? openOAuthPopup() : null;

      createMutation.mutate(payload, {
        onSuccess: (response) => {
          const authorizationUrl = response.oauth_init?.authorization_url;
          const { redirected, blocked } = settleOAuthPopup(
            popup,
            authorizationUrl,
          );
          if (redirected) {
            toast.success(
              t("mcpPage.toasts.addedAuthorize"),
            );
          } else if (blocked && authorizationUrl) {
            toast.warning(
              t("mcpPage.toasts.popupBlocked", { url: authorizationUrl }),
            );
          }
        },
        onError: () => {
          popup?.close();
        },
      });
    },
    [dialogMode, editingId, updateMutation, createMutation, t],
  );

  // renderedId lags selectedId on close so the pane can animate out before it
  // leaves the tree.
  const { renderedId, closing, onExited } = usePanePresence(selectedId);
  const detailOpen = Boolean(renderedId);

  return (
    <>
      {/*
        Two panes filling the content area, not a panel floating next to a list.
        The divider is the left pane's trailing border, so there is exactly one
        line and it sits between them; it only appears when detail is open,
        otherwise it would draw down the far right edge of an empty row.

        Each pane scrolls on its own (min-h-0 + overflow-y-auto). The page itself
        never scrolls, which is what keeps both headers fixed in place while a
        long tool list moves underneath.
      */}
      <div className="flex h-full min-h-0 overflow-hidden">
        <div
          className={cn(
            "flex min-w-0 flex-1 flex-col",
            detailOpen && "border-e border-border",
          )}
        >
          <div className="shrink-0 px-4 py-4">
            <MCPToolbar
              query={query}
              onQueryChange={setQuery}
              onNewConnection={handleNewConnection}
            />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
            <MCPList
              connections={filtered}
              selectedId={selectedId}
              onSelect={handleSelect}
              onEdit={handleEdit}
              onDelete={handleDelete}
              isLoading={isLoading}
              errorMessage={error ? t("mcpPage.errors.loadConnections") : null}
            />
          </div>
        </div>

        {renderedId && (
          // The animation is on mount and on the closing flag, so it plays when
          // the pane opens and closes but not when the user moves between rows —
          // the wrapper stays mounted while only connectionId changes. Opening is
          // a change of layout; switching is a change of content.
          <div
            className={cn(
              "min-w-0 flex-1 overflow-y-auto",
              closing ? "animate-pane-out" : "animate-pane-in",
            )}
            // Animations inside the pane bubble up here too, and any one of them
            // would unmount it mid-open.
            onAnimationEnd={(event) => {
              if (event.target === event.currentTarget && closing) onExited();
            }}
          >
            <MCPDetailPanel onClose={closeAll} connectionId={renderedId} />
          </div>
        )}
      </div>

      <AddConnectorDialog
        open={dialogOpen}
        mode={dialogMode}
        initialValues={editInitialValues}
        onOpenChange={handleDialogOpenChange}
        onSubmit={handleDialogSubmit}
        isSubmitting={dialogSubmitting}
      />

      <DeleteConnectionDialog
        connection={pendingDeleteConnection}
        isDeleting={deleteMutation.isPending}
        onCancel={closeAll}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default MCPsPage;
